import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useLiveRefresh } from "@/lib/live";
import { useHub } from "@/components/AppShell";
import { Loader, PageHeader } from "@/components/common";
import { BLUE, CATEGORIES, CAT_RGB, addDays, deviceTz, matches, tzShort, ymd } from "@/lib/calendar";
import { AgendaView, MonthView, WeekView } from "@/components/calendar/Views";
import { EventDetails, EventForm } from "@/components/calendar/EventDialog";
import { VoiceButton } from "@/components/voice/VoiceButton";

function range(view, a) {
  if (view === "month") { const f = new Date(a.getFullYear(), a.getMonth(), 1); const s = addDays(f, -f.getDay()); return [s, addDays(s, 41)]; }
  if (view === "week") { const s = addDays(a, -a.getDay()); return [s, addDays(s, 6)]; }
  return [a, addDays(a, 30)];
}

function title(view, a) {
  if (view === "month") return a.toLocaleDateString([], { month: "long", year: "numeric" });
  const [s, e] = range(view, a);
  return `${s.toLocaleDateString([], { month: "short", day: "numeric" })} – ${e.toLocaleDateString([], { month: "short", day: "numeric" })}`;
}

export default function Calendar() {
  const { hub } = useHub();
  const [meta, setMeta] = useState(null);
  const [view, setView] = useState(() => (window.innerWidth < 768 ? "agenda" : "month"));
  const [anchor, setAnchor] = useState(() => new Date(new Date().setHours(0, 0, 0, 0)));
  const [events, setEvents] = useState(null);
  const [cats, setCats] = useState([]);
  const [member, setMember] = useState("");
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState(null);
  const [s, e] = range(view, anchor);
  const load = useCallback(() => api.get(`/organizations/${hub.id}/calendar/events`, { params: { start: ymd(addDays(s, -1)), end: ymd(addDays(e, 1)) } })
    .then(({ data }) => setEvents(data.events)).catch(() => setEvents([])), [hub.id, view, anchor]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get(`/organizations/${hub.id}/calendar/people`).then(({ data }) => { window.__hubTz = data.timezone; setMeta(data); }); }, [hub.id]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load, (m) => m.organization_id === hub.id && m.entity === "calendar.event");
  const shown = useMemo(() => (events || []).filter((x) => matches(x, cats, member)), [events, cats, member]);
  if (!meta || !events) return <Loader />;
  const step = (n) => setAnchor((a) => (view === "month" ? new Date(a.getFullYear(), a.getMonth() + n, 1) : addDays(a, n * (view === "week" ? 7 : 30))));
  const done = () => { setForm(null); setOpen(null); load(); };
  return (
    <div data-testid="calendar-page">
      <PageHeader eyebrow="Calendar" title="Family Calendar" rgb={BLUE} testid="calendar-title"
        actions={<div className="flex gap-2"><VoiceButton hubId={hub.id} screen="calendar" onSaved={load} />{meta.can_edit && <button className="q-btn q-btn-primary" onClick={() => setForm({ day: ymd(anchor) })} data-testid="cal-new-event-btn"><Plus className="h-4 w-4" /> New event</button>}</div>} />
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-full border border-q-line p-1" role="tablist">
          {["month", "week", "agenda"].map((v) => <button key={v} onClick={() => setView(v)} role="tab" aria-selected={view === v} data-testid={`cal-view-${v}`}
            className={`min-h-[44px] rounded-full px-4 text-sm font-bold capitalize transition-colors duration-150 ${view === v ? "text-white" : "text-q-muted"}`} style={view === v ? { background: `rgb(${BLUE})` } : undefined}>{v}</button>)}
        </div>
        <button className="q-btn q-btn-secondary min-h-[44px]" onClick={() => setAnchor(new Date(new Date().setHours(0, 0, 0, 0)))} data-testid="cal-today-btn">Today</button>
        <button className="q-btn q-btn-ghost h-11 w-11 p-0" onClick={() => step(-1)} aria-label="Previous" data-testid="cal-prev-btn"><ChevronLeft className="h-5 w-5" /></button>
        <button className="q-btn q-btn-ghost h-11 w-11 p-0" onClick={() => step(1)} aria-label="Next" data-testid="cal-next-btn"><ChevronRight className="h-5 w-5" /></button>
        <h2 className="font-display text-lg font-extrabold text-q-text" data-testid="cal-range-title">{title(view, anchor)}</h2>
        {deviceTz() !== meta.timezone && <span className="text-xs text-q-faint" data-testid="cal-tz-label">Shown in your time ({tzShort(deviceTz())})</span>}
      </div>
      <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="cal-filters">
        {CATEGORIES.map((c) => {
          const on = cats.includes(c);
          return <button key={c} onClick={() => setCats((p) => (on ? p.filter((x) => x !== c) : [...p, c]))} aria-pressed={on} data-testid={`cal-filter-${c.toLowerCase()}`}
            className="min-h-[44px] rounded-full border px-3 text-sm font-semibold transition-colors duration-150"
            style={{ borderColor: `rgba(${CAT_RGB[c]},${on ? 0.9 : 0.3})`, background: on ? `rgba(${CAT_RGB[c]},0.18)` : "transparent", color: on ? `rgb(${CAT_RGB[c]})` : undefined }}>{c}</button>;
        })}
        <select className="q-input w-auto min-h-[44px]" value={member} onChange={(x) => setMember(x.target.value)} data-testid="cal-filter-member">
          <option value="">Everyone</option>
          {meta.people.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
        </select>
      </div>
      {view === "month" && <MonthView anchor={anchor} events={shown} onOpen={setOpen} onDay={(d) => { setAnchor(d); setView("agenda"); }} />}
      {view === "week" && <WeekView anchor={anchor} events={shown} onOpen={setOpen} />}
      {view === "agenda" && <AgendaView events={shown} onOpen={setOpen} people={meta.people} />}
      {open && !form && <EventDetails hubId={hub.id} meta={meta} e={open} onClose={() => setOpen(null)} onEdit={() => setForm({ ev: open })} onChanged={done} />}
      {form && <EventForm hubId={hub.id} meta={meta} ev={form.ev} day={form.day} onClose={() => setForm(null)} onSaved={done} />}
    </div>
  );
}
